import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, Menu, MessageCircle, Moon, ShieldUser, Sun } from 'lucide-react'
import { useCategories } from '@/features/categories/hooks/use-categories'
import { whatsAppChatUrl } from '@/lib/whatsapp'
import { STORE_WHATSAPP_NUMBER } from '@/lib/env'
import { cn } from '@/lib/utils'
import type { Category } from '@/types'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { ICON_BUTTON_CLASS } from './icon-button'

interface CategoryDrawerProps {
  /** Nodo con el tema del storefront — ver PublicLayout, mismo motivo que CartDrawer. */
  container?: HTMLDivElement | null
  isDark: boolean
  onToggleDark: () => void
}

export function CategoryDrawer({ container, isDark, onToggleDark }: CategoryDrawerProps) {
  const [open, setOpen] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const categories = useCategories()

  function close() {
    setOpen(false)
    setExpandedId(null)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" aria-label="Abrir categorías" className={ICON_BUTTON_CLASS}>
          <Menu className="size-4.5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" container={container} className="w-4/5 max-w-xs gap-0 p-0">
        <SheetHeader className="border-b border-border/70">
          <SheetTitle>Categorías</SheetTitle>
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto p-2">
          {(categories.data ?? []).map((category) => (
            <CategoryRow
              key={category.id}
              category={category}
              expanded={expandedId === category.id}
              onToggle={() => setExpandedId((current) => (current === category.id ? null : category.id))}
              onNavigate={close}
            />
          ))}
        </nav>

        <div className="mt-auto grid gap-1 border-t border-border/70 p-3">
          <a
            href={whatsAppChatUrl(STORE_WHATSAPP_NUMBER)}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-lg px-2 py-2.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            <MessageCircle className="size-4 text-[#25D366]" />
            Escríbenos por WhatsApp
          </a>
          <Link
            to="/login"
            onClick={close}
            className="flex items-center gap-2 rounded-lg px-2 py-2.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            <ShieldUser className="size-4 text-muted-foreground" />
            Administrador
          </Link>
          {/* Visible solo en celular — en escritorio el mismo toggle ya está en el header. */}
          <button
            type="button"
            onClick={onToggleDark}
            className="flex items-center gap-2 rounded-lg px-2 py-2.5 text-left text-sm font-medium text-foreground hover:bg-muted sm:hidden"
          >
            {isDark ? <Sun className="size-4 text-muted-foreground" /> : <Moon className="size-4 text-muted-foreground" />}
            {isDark ? 'Modo claro' : 'Modo oscuro'}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function CategoryRow({
  category,
  expanded,
  onToggle,
  onNavigate,
}: {
  category: Category
  expanded: boolean
  onToggle: () => void
  onNavigate: () => void
}) {
  const subcategories = category.subcategories ?? []
  const hasSubcategories = subcategories.length > 0

  return (
    <div>
      <div className="flex items-center">
        <Link
          to={`/?categoria=${category.id}`}
          onClick={onNavigate}
          className="flex-1 rounded-lg px-2 py-2.5 text-[15px] font-medium text-foreground hover:bg-muted"
        >
          {category.name}
        </Link>
        {hasSubcategories && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? `Ocultar subcategorías de ${category.name}` : `Ver subcategorías de ${category.name}`}
            className="flex size-9 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
          </button>
        )}
      </div>

      {hasSubcategories && expanded && (
        <div className="ml-3 grid gap-0.5 border-l border-border/70 pl-3">
          {subcategories.map((sub) => (
            <Link
              key={sub.id}
              to={`/?categoria=${sub.id}`}
              onClick={onNavigate}
              className="rounded-lg px-2 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {sub.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
